(() => {
  if (customElements.get('aotr-header-controller')) return;

  class AotrHeaderController extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.header = this.closest('.aotr-header') || this.querySelector('.aotr-header') || this;
      this.menus = [...this.header.querySelectorAll('.aotr-nav-details')];
      this.mobileHome = this.querySelector('#NavDrawer [data-aotr-menu-home]');
      this.mobileScroller = this.querySelector('#NavDrawer .aotr-drawer-body');
      this.mobilePanels = [...this.querySelectorAll('#NavDrawer [data-aotr-menu-panel]')];
      this.mobilePanelTriggers = [...this.querySelectorAll('#NavDrawer [data-aotr-panel-open]')];
      this.hoverMedia = window.matchMedia('(min-width: 981px) and (hover: hover) and (pointer: fine)');

      this.mobilePanelTriggers.forEach(trigger => {
        trigger.addEventListener('click', event => {
          event.preventDefault();
          this.openMobilePanel(trigger.getAttribute('data-aotr-panel-open'), trigger);
        }, { signal });
      });

      this.querySelectorAll('#NavDrawer [data-aotr-panel-back]').forEach(button => {
        button.addEventListener('click', event => {
          event.preventDefault();
          this.closeMobilePanel();
        }, { signal });
      });

      document.addEventListener('drawerOpen.NavDrawer', () => {
        this.resetMobileMenu();
      }, { signal });
      this.resetMobileMenu();

      this.menus.forEach(menu => {
        const summary = menu.querySelector(':scope > summary');
        const toggle = summary?.querySelector('.aotr-nav-toggle');
        if (!summary) return;

        const sync = () => {
          const expanded = String(menu.open);
          summary.setAttribute('aria-expanded', expanded);
          if (toggle) toggle.setAttribute('aria-expanded', expanded);
        };

        sync();
        menu.addEventListener('toggle', () => {
          if (menu.open) this.closeMenus(menu);
          sync();
        }, { signal });

        menu.addEventListener('pointerenter', () => {
          if (this.hoverMedia.matches) this.openMenu(menu);
        }, { signal });

        summary.addEventListener('click', event => {
          const link = event.target.closest('a');
          if (link) {
            event.stopPropagation();
            return;
          }
          if (event.target.closest('.aotr-nav-toggle')) {
            event.preventDefault();
            event.stopPropagation();
            if (this.hoverMedia.matches && event.detail > 0) this.openMenu(menu);
            else if (menu.open) this.closeMenus();
            else this.openMenu(menu);
          }
        }, { signal });

        summary.addEventListener('keydown', event => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.openMenu(menu);
            menu.querySelector('.aotr-mega a[href]')?.focus();
          }
          if (event.key === 'ArrowUp') {
            event.preventDefault();
            this.openMenu(menu);
            const links = menu.querySelectorAll('.aotr-mega a[href]');
            links[links.length - 1]?.focus();
          }
        }, { signal });
      });

      this.header.addEventListener('pointerleave', () => {
        if (this.hoverMedia.matches && !this.header.contains(document.activeElement)) this.closeMenus();
      }, { signal });

      this.header.addEventListener('focusout', () => {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
          if (!this.header.contains(document.activeElement)) this.closeMenus();
        }, 0);
      }, { signal });

      document.addEventListener('pointerdown', event => {
        if (!this.header.contains(event.target)) this.closeMenus();
      }, { signal });

      document.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const menu = event.target.closest('.aotr-nav-details[open]') || this.menus.find(item => item.open);
        if (!menu) return;
        const restoreFocus = this.header.contains(document.activeElement);
        this.closeMenus();
        if (restoreFocus) {
          event.preventDefault();
          event.stopPropagation();
          (menu.querySelector('.aotr-nav-toggle') || menu.querySelector('summary'))?.focus();
        }
      }, { signal });

      const drawerSearch = this.querySelector('.aotr-drawer-search');
      drawerSearch?.addEventListener('click', event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        clearTimeout(this.searchTimer);
        this.closeMenus();
        if (window.theme?.NavDrawer) window.theme.NavDrawer.close();
        else this.querySelector('#NavDrawer .js-drawer-close')?.click();
        this.searchTimer = setTimeout(() => {
          this.header.querySelector('.aotr-desktop-search.js-search-header')?.click();
        }, 550);
      }, { capture: true, signal });

      this.searchContainer = this.header.querySelector('.site-header__search-container');
      this.updateSearchViewport = () => {
        if (!this.searchOpen || !this.searchContainer) return;
        const viewport = window.visualViewport;
        this.searchContainer.style.setProperty('--aotr-search-viewport-top', `${viewport?.offsetTop || 0}px`);
        this.searchContainer.style.setProperty('--aotr-search-viewport-height', `${viewport?.height || window.innerHeight}px`);
      };
      document.addEventListener('predictive-search:open', event => {
        if (event.detail?.context !== 'header') return;
        this.closeMenus();
        this.searchOpen = true;
        this.updateSearchViewport();
      }, { signal });
      document.addEventListener('predictive-search:close-all', () => {
        this.searchOpen = false;
        this.searchContainer?.style.removeProperty('--aotr-search-viewport-top');
        this.searchContainer?.style.removeProperty('--aotr-search-viewport-height');
      }, { signal });
      window.addEventListener('resize', this.updateSearchViewport, { passive: true, signal });
      window.visualViewport?.addEventListener('resize', this.updateSearchViewport, { passive: true, signal });
      window.visualViewport?.addEventListener('scroll', this.updateSearchViewport, { passive: true, signal });

      this.hoverMedia.addEventListener('change', () => this.closeMenus(), { signal });
      this.updateScroll = () => {
        this.header.classList.toggle('aotr-header--scrolled', window.scrollY > 6);
      };
      window.addEventListener('scroll', this.updateScroll, { passive: true, signal });
      this.updateScroll();
    }

    openMenu(menu) {
      this.closeMenus(menu);
      menu.open = true;
    }

    closeMenus(except) {
      this.menus.forEach(menu => {
        if (menu !== except) menu.open = false;
      });
    }

    openMobilePanel(panelId, trigger) {
      const panel = this.mobilePanels.find(item => item.id === panelId);
      if (!panel || !this.mobileHome || !this.mobileScroller) return;
      if (!this.mobileActivePanel) this.mobileHomeScroll = this.mobileScroller.scrollTop;
      this.mobileActivePanel = panel;
      this.mobileActiveTrigger = trigger;
      this.mobileHome.hidden = true;
      this.mobilePanels.forEach(item => {
        item.hidden = item !== panel;
      });
      this.mobilePanelTriggers.forEach(button => {
        button.setAttribute('aria-expanded', String(button.getAttribute('data-aotr-panel-open') === panelId));
      });
      this.mobileScroller.scrollTop = 0;
      const heading = panel.querySelector('[data-aotr-panel-title]') || panel.querySelector('[data-aotr-panel-back]');
      heading?.focus({ preventScroll: true });
    }

    closeMobilePanel() {
      if (!this.mobileActivePanel) return;
      const trigger = this.mobileActiveTrigger;
      this.mobileActivePanel = null;
      this.mobileActiveTrigger = null;
      if (this.mobileHome) this.mobileHome.hidden = false;
      this.mobilePanels.forEach(panel => {
        panel.hidden = true;
      });
      this.mobilePanelTriggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (this.mobileScroller) this.mobileScroller.scrollTop = this.mobileHomeScroll;
      trigger?.focus({ preventScroll: true });
    }

    resetMobileMenu() {
      this.mobileActivePanel = null;
      this.mobileActiveTrigger = null;
      this.mobileHomeScroll = 0;
      if (this.mobileHome) this.mobileHome.hidden = false;
      this.mobilePanels.forEach(panel => {
        panel.hidden = true;
        panel.querySelectorAll('.aotr-mobile-subgroup').forEach(group => {
          group.open = false;
        });
      });
      this.mobilePanelTriggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      if (this.mobileScroller) this.mobileScroller.scrollTop = 0;
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.abortController = null;
      clearTimeout(this.focusTimer);
      clearTimeout(this.searchTimer);
      this.searchOpen = false;
      this.searchContainer?.style.removeProperty('--aotr-search-viewport-top');
      this.searchContainer?.style.removeProperty('--aotr-search-viewport-height');
    }
  }

  customElements.define('aotr-header-controller', AotrHeaderController);
})();
